import {
  Badge,
  confirm,
  createExpandableColumn,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { formatDateLocal } from "@/shared/lib/date-time";
import {
  CheckIcon,
  CopyIcon,
  EditIcon,
  LinkIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import { RowActions } from "@/shared/ui/RowActions";
import type { KPIListItem } from "../../domain/types";
import {
  getKpiLevelLabel,
  KPI_LEVEL_BADGE_VARIANT,
  type KpiLevel,
} from "../utils/kpi-level-labels";

type KPITableProps = {
  canManage: boolean;
  canCreate: boolean;
  isLoading: boolean;
  data: KPIListItem[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onEdit?: (kpi: KPIListItem) => void;
  onCopy: (id: string) => Promise<void>;
  onActivate?: (id: string) => Promise<void>;
  onDeactivate: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function KPITable({
  canManage,
  canCreate,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onEdit,
  onCopy,
  onActivate,
  onDeactivate,
  onDelete,
  onPaginationChange,
  onSortingChange,
}: KPITableProps) {
  const nav = useNavigate();
  const getLevelBadge = (level: string) => {
    const key = level as KpiLevel;
    const variant = KPI_LEVEL_BADGE_VARIANT[key] ?? "default";
    return (
      <Badge variant={variant} className="text-xs">
        {getKpiLevelLabel(level)}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const config = {
      active: { label: "ກຳລັງໃຊ້ງານ", variant: "default" as const },
      draft: { label: "ຮ່າງ", variant: "secondary" as const },
      inactive: { label: "ປິດການໃຊ້ງານ", variant: "outline" as const },
    };
    const cfg = config[status as keyof typeof config] || config.draft;
    return (
      <Badge variant={cfg.variant} className="text-xs">
        {cfg.label}
      </Badge>
    );
  };

  const columns: TanstackReactTable.ColumnDef<KPIListItem>[] = [
    createExpandableColumn<KPIListItem>(""),
    createSortableColumn<KPIListItem>("name", "ຊື່ KPI", { size: 150 }),
    {
      id: "level",
      header: "ລະດັບ",
      cell: ({ row }) => getLevelBadge(row.original.level),
      size: 120,
    },
    {
      id: "description",
      header: "ວັດແທກແນວໃດ",
      cell: ({ row }) => (
        <div className="max-w-[300px] truncate text-sm">
          {row.original.description || "-"}
        </div>
      ),
      size: 200,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => getStatusBadge(row.original.status),
      size: 120,
    },
    {
      id: "updatedAt",
      header: "ວັນທີອັບເດດ",
      cell: ({ row }) => {
        const date = row.original.updatedAt;
        if (!date) return "-";
        try {
          return formatDateLocal(date);
        } catch {
          return date;
        }
      },
      size: 150,
    },
    {
      id: "assignments",
      header: "ການກຳນົດ",
      cell: ({ row }) => {
        const count = row.original.assignmentCount || 0;
        if (count === 0) {
          return <span className="text-muted-foreground text-xs">-</span>;
        }
        return (
          <Badge variant="outline" className="gap-1 text-xs">
            <LinkIcon className="h-3 w-3" />
            {count}
          </Badge>
        );
      },
      size: 120,
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const kpi = row.original;
        const id = kpi.id;
        const actions = [];

        actions.push({
          label: "ເບິ່ງ",
          icon: <LinkIcon className="h-4 w-4" />,
          onClick: () => {
            nav({ to: `/app/kpi/${id}` });
          },
        });

        if (canManage) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => {
              if (onEdit) {
                onEdit(kpi);
              } else {
                nav({ to: `/app/kpi/${id}/edit` });
              }
            },
          });
        }

        if (canCreate) {
          actions.push({
            label: "ຄັດລອກ",
            icon: <CopyIcon className="h-4 w-4" />,
            onClick: async () => {
              await onCopy(id);
            },
          });
        }

        if (canManage) {
          if (kpi.status === "active") {
            actions.push({
              label: "ປິດການໃຊ້ງານ",
              icon: <XIcon className="h-4 w-4" />,
              onClick: async () => {
                const ok = await confirm({
                  title: "ປິດການໃຊ້ງານ KPI",
                  description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະປິດການໃຊ້ງານ KPI ນີ້?",
                  actionText: "ປິດການໃຊ້ງານ",
                  ActionProps: {
                    variant: "default",
                  },
                });
                if (ok) await onDeactivate(id);
              },
            });
          } else if (kpi.status === "inactive" && onActivate) {
            actions.push({
              label: "ເປີດການໃຊ້ງານ",
              icon: <CheckIcon className="h-4 w-4" />,
              onClick: async () => {
                const ok = await confirm({
                  title: "ເປີດການໃຊ້ງານ KPI",
                  description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະເປີດການໃຊ້ງານ KPI ນີ້?",
                  actionText: "ເປີດການໃຊ້ງານ",
                  ActionProps: {
                    variant: "default",
                  },
                });
                if (ok) await onActivate(id);
              },
            });
          }
        }

        if (canManage) {
          actions.push({
            label: "ລຶບ",
            variant: "destructive" as const,
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບ KPI",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ KPI ນີ້? ການລຶບນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.",
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) await onDelete(id);
            },
          });
        }

        return <RowActions actions={actions} maxInline={3} />;
      },
      size: 100,
    },
  ];

  return (
    <div data-tourid="kpi-table">
      <DataTable<KPIListItem, unknown>
        noDataMessage="ບໍ່ພົບ KPI"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(pagination) =>
          onPaginationChange(pagination.offset, pagination.limit)
        }
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
        renderExpandedContent={({ original }) => {
          return (
            <div className="space-y-4 rounded-lg bg-muted/50 p-4">
              <div>
                <h4 className="mb-2 font-medium text-sm">ຄຳອະທິບາຍ</h4>
                <p className="text-muted-foreground text-sm">
                  {original.description || "ບໍ່ມີຄຳອະທິບາຍ"}
                </p>
              </div>
              <div>
                <h4 className="mb-2 font-medium text-sm">ວິທີການວັດ</h4>
                <p className="text-muted-foreground text-sm">
                  {original.measurementMethod || "-"}
                </p>
              </div>
              {original.targetValue != null && (
                <div>
                  <h4 className="mb-2 font-medium text-sm">ເປົ້າໝາຍ</h4>
                  <p className="text-muted-foreground text-sm">
                    {typeof original.targetValue === "number"
                      ? original.targetValue.toLocaleString()
                      : Number(original.targetValue || 0).toLocaleString()}
                  </p>
                </div>
              )}
              {original.measurementSource && (
                <div>
                  <h4 className="mb-2 font-medium text-sm">ແຫຼ່ງຂໍ້ມູນ</h4>
                  <p className="text-muted-foreground text-sm">
                    {original.measurementSource}
                  </p>
                </div>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}
